import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Space } from "antd";
import { CloseOutlined, MinusOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const QuantityControl = ({
  productId,
  currentQuantity,
  onIncrease,
  onDecrease,
  onManualInput,
  additionalDisplay = null,
  disabled = false,
  isMobile = false,
  allowNegative = false,
  inputRef: externalInputRef = null,
  onTabPress = null,
  startInManualMode = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isManualMode, setIsManualMode] = useState(startInManualMode);
  const [inputValue, setInputValue] = useState(startInManualMode ? currentQuantity.toString() : "");
  const [pressedButton, setPressedButton] = useState(null);
  const [isInputSelected, setIsInputSelected] = useState(false);
  const internalInputRef = useRef(null);

  // Check localStorage for on-screen keyboard preference
  const [showOnScreenKeyboard, setShowOnScreenKeyboard] = useState(() => {
    const saved = localStorage.getItem("onScreenKeyboardEnabled");
    return saved !== "false"; // Default to true
  });

  // Use external ref if provided, otherwise use internal ref
  const inputRef = externalInputRef || internalInputRef;

  // Navigate to settings page
  const handleNavigateToSettings = () => {
    navigate("/cms/profile#tools");
  };

  // Reset to startInManualMode when prop changes
  useEffect(() => {
    setIsManualMode(startInManualMode);
    setInputValue(startInManualMode ? currentQuantity.toString() : "");
  }, [startInManualMode, productId]);

  // Reset input value when currentQuantity changes externally (e.g., validation failed)
  useEffect(() => {
    if (isManualMode && inputValue !== "") {
      const currentInputNum = parseInt(inputValue, 10);
      if (!isNaN(currentInputNum) && currentInputNum !== currentQuantity) {
        // Input value differs from actual quantity - validation must have failed
        setInputValue(currentQuantity.toString());
        // Re-select the text for easy correction
        if (inputRef.current) {
          setTimeout(() => {
            inputRef.current.select();
          }, 0);
        }
      }
    }
  }, [currentQuantity, isManualMode, inputValue]);

  // Auto-focus and select when entering manual mode
  useEffect(() => {
    if (isManualMode && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isManualMode]);

  // Listen for localStorage changes (e.g., from settings page)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("onScreenKeyboardEnabled");
      setShowOnScreenKeyboard(saved !== "false");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleWheel = (e) => {
    e.target.blur();
  };

  const handleManualToggle = () => {
    if (!isManualMode) {
      // Entering manual mode - set current quantity as input value
      setInputValue(currentQuantity.toString());
    } else {
      // Exiting manual mode - clear input value
      setInputValue("");
    }
    setIsManualMode(!isManualMode);
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    setIsInputSelected(false); // Clear selection state when typing

    // If empty, don't process yet
    if (value === "") {
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && (allowNegative || numValue >= 0)) {
      onManualInput(productId, numValue);
    }
  };

  const handleInputBlur = (e) => {
    // If empty on blur, set to 0 (or current quantity for negative mode)
    if (e.target.value === "") {
      const defaultValue = allowNegative ? currentQuantity : 0;
      onManualInput(productId, defaultValue);
      setInputValue(defaultValue.toString());
    }
  };

  const handleInputFocus = () => {
    // When focusing, set the input value to the current quantity if not already set
    if (inputValue === "") {
      setInputValue(currentQuantity.toString());
    }
    // Auto-select the text
    if (inputRef.current) {
      inputRef.current.select();
      setIsInputSelected(true);
    }
  };

  // Force keyboard to open on iOS even with Bluetooth scanner connected
  const handleInputClick = (e) => {
    if (inputRef.current) {
      // Blur and refocus to force keyboard on iOS
      inputRef.current.blur();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.click();
          setIsInputSelected(true);
        }
      }, 10);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab" && onTabPress) {
      e.preventDefault();
      onTabPress(productId, e.shiftKey);
    }
  };

  // Touch feedback handlers
  const handleButtonPress = (buttonType, action) => {
    setPressedButton(buttonType);
    action();
    // Clear pressed state after short delay
    setTimeout(() => setPressedButton(null), 150);
  };

  // Handle keypad button press
  const handleKeypadPress = (value) => {
    if (disabled) return;

    if (value === "backspace") {
      setIsInputSelected(false);
      const newValue = inputValue.slice(0, -1);
      setInputValue(newValue);
      if (newValue === "" || newValue === "-") {
        const defaultValue = allowNegative ? currentQuantity : 0;
        onManualInput(productId, defaultValue);
      } else {
        const numValue = parseInt(newValue, 10);
        if (!isNaN(numValue)) {
          onManualInput(productId, numValue);
        }
      }
    } else if (value === "clear") {
      setIsInputSelected(false);
      setInputValue("");
      const defaultValue = allowNegative ? currentQuantity : 0;
      onManualInput(productId, defaultValue);
    } else if (value === "-" && allowNegative) {
      setIsInputSelected(false);
      // Toggle negative sign
      if (inputValue.startsWith("-")) {
        const newValue = inputValue.slice(1);
        setInputValue(newValue);
        const numValue = parseInt(newValue, 10);
        if (!isNaN(numValue)) {
          onManualInput(productId, numValue);
        }
      } else {
        const newValue = "-" + inputValue;
        setInputValue(newValue);
        const numValue = parseInt(newValue, 10);
        if (!isNaN(numValue)) {
          onManualInput(productId, numValue);
        }
      }
    } else {
      // Number button pressed
      // If input is selected, replace the value instead of appending (like native keyboard)
      const newValue = isInputSelected ? value : inputValue + value;
      setInputValue(newValue);
      setIsInputSelected(false); // Clear selection after first keypress
      const numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && (allowNegative || numValue >= 0)) {
        onManualInput(productId, numValue);
      }
    }
  };

  if (isManualMode) {
    return (
      <>
        <Space size={isMobile ? "large" : "small"}>
          <StyledInput
            ref={inputRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min={allowNegative ? undefined : 0}
            onWheel={handleWheel}
            disabled={disabled}
            readOnly={false}
            $isMobile={isMobile}
          />
          <MobileButton
            icon={<CloseOutlined />}
            onClick={handleManualToggle}
            size={isMobile ? "large" : "small"}
            type="text"
            disabled={disabled}
            $isMobile={isMobile}
          />
        </Space>

        {/* Fixed on-screen numeric keypad at bottom of screen - only show on mobile and if enabled in settings */}
        {isMobile && showOnScreenKeyboard && (
          <FixedKeypadOverlay $isMobile={isMobile} onClick={handleManualToggle}>
            <NumericKeypad $isMobile={isMobile} onClick={(e) => e.stopPropagation()}>
              <KeypadHeader>
                <SettingsKeypadButton onClick={handleNavigateToSettings} $isMobile={isMobile}>
                  <SettingOutlined />
                </SettingsKeypadButton>
                <CloseKeypadButton onClick={handleManualToggle} $isMobile={isMobile}>
                  <CloseOutlined />
                </CloseKeypadButton>
              </KeypadHeader>
              <KeypadRow>
                <KeypadButton onClick={() => handleKeypadPress("1")} $isMobile={isMobile}>
                  1
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("2")} $isMobile={isMobile}>
                  2
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("3")} $isMobile={isMobile}>
                  3
                </KeypadButton>
              </KeypadRow>
              <KeypadRow>
                <KeypadButton onClick={() => handleKeypadPress("4")} $isMobile={isMobile}>
                  4
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("5")} $isMobile={isMobile}>
                  5
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("6")} $isMobile={isMobile}>
                  6
                </KeypadButton>
              </KeypadRow>
              <KeypadRow>
                <KeypadButton onClick={() => handleKeypadPress("7")} $isMobile={isMobile}>
                  7
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("8")} $isMobile={isMobile}>
                  8
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("9")} $isMobile={isMobile}>
                  9
                </KeypadButton>
              </KeypadRow>
              <KeypadRow>
                {allowNegative && (
                  <KeypadButton onClick={() => handleKeypadPress("-")} $isMobile={isMobile}>
                    +/-
                  </KeypadButton>
                )}
                <KeypadButton onClick={() => handleKeypadPress("0")} $isMobile={isMobile}>
                  0
                </KeypadButton>
                <KeypadButton onClick={() => handleKeypadPress("backspace")} $isMobile={isMobile}>
                  ←
                </KeypadButton>
              </KeypadRow>
            </NumericKeypad>
          </FixedKeypadOverlay>
        )}
      </>
    );
  }

  return (
    <QuantityChooser $isMobile={isMobile}>
      <ControlButton
        onClick={() => handleButtonPress("decrease", () => onDecrease(productId))}
        disabled={disabled || (!allowNegative && currentQuantity <= 0)}
        $isMobile={isMobile}
        $isPressed={pressedButton === "decrease"}
      >
        <MinusOutlined />
      </ControlButton>
      <QuantityDisplay $isMobile={isMobile} onClick={!disabled ? handleManualToggle : undefined} $clickable={!disabled}>
        {currentQuantity}
        {additionalDisplay}
      </QuantityDisplay>
      <ControlButton
        onClick={() => handleButtonPress("increase", () => onIncrease(productId))}
        disabled={disabled}
        $isMobile={isMobile}
        $isPressed={pressedButton === "increase"}
      >
        <PlusOutlined />
      </ControlButton>
    </QuantityChooser>
  );
};

const FixedKeypadOverlay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: ${(props) => (props.$isMobile ? "16px" : "12px")};
  padding-bottom: ${(props) => (props.$isMobile ? "max(16px, env(safe-area-inset-bottom))" : "12px")};
  background: linear-gradient(to top, rgba(0, 0, 0, 0.05) 0%, transparent 100%);
  cursor: pointer;
`;

const NumericKeypad = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$isMobile ? "8px" : "6px")};
  padding: ${(props) => (props.$isMobile ? "12px 16px 16px" : "8px 12px 12px")};
  background-color: #ffffff;
  border-radius: ${(props) => (props.$isMobile ? "16px 16px 0 0" : "12px")};
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
  user-select: none;
  max-width: ${(props) => (props.$isMobile ? "100%" : "400px")};
  width: 100%;
  cursor: default;
`;

const KeypadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const SettingsKeypadButton = styled.button`
  width: ${(props) => (props.$isMobile ? "36px" : "28px")};
  height: ${(props) => (props.$isMobile ? "36px" : "28px")};
  font-size: ${(props) => (props.$isMobile ? "18px" : "14px")};
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: #bfbfbf; /* Light grey */
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f5f5f5;
    color: #8c8c8c;
  }

  &:active {
    background-color: #e6e6e6;
    transform: scale(0.95);
  }

  -webkit-tap-highlight-color: transparent;
`;

const CloseKeypadButton = styled.button`
  width: ${(props) => (props.$isMobile ? "36px" : "28px")};
  height: ${(props) => (props.$isMobile ? "36px" : "28px")};
  font-size: ${(props) => (props.$isMobile ? "18px" : "14px")};
  border: none;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #e6e6e6;
    color: rgba(0, 0, 0, 0.88);
  }

  &:active {
    background-color: #d9d9d9;
    transform: scale(0.95);
  }

  -webkit-tap-highlight-color: transparent;
`;

const KeypadRow = styled.div`
  display: flex;
  gap: ${(props) => (props.$isMobile ? "8px" : "6px")};
  justify-content: center;
`;

const KeypadButton = styled.button`
  width: ${(props) => {
    if (props.$wide) return props.$isMobile ? "200px" : "140px";
    return props.$isMobile ? "90px" : "65px";
  }};
  height: ${(props) => (props.$isMobile ? "50px" : "38px")};
  font-size: ${(props) => (props.$isMobile ? "24px" : "18px")};
  font-weight: 500;
  border: 1px solid #d9d9d9;
  border-radius: ${(props) => (props.$isMobile ? "8px" : "6px")};
  background-color: #ffffff;
  color: rgba(0, 0, 0, 0.88);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Touch feedback */
  &:active {
    background-color: #e6e6e6;
    transform: scale(0.95);
  }

  &:hover {
    background-color: #fafafa;
    border-color: #4096ff;
  }

  /* Prevent text selection */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  /* Prevent tap highlight on mobile */
  -webkit-tap-highlight-color: transparent;
`;

const StyledInput = styled(Input)`
  width: ${(props) => (props.$isMobile ? "90px" : "60px")};
  height: ${(props) => (props.$isMobile ? "48px" : "32px")};
  text-align: center;
  font-size: ${(props) => (props.$isMobile ? "20px" : "14px")};

  /* Prevent zoom on mobile */
  ${(props) =>
    props.$isMobile &&
    `
    font-size: 16px !important; /* iOS requires 16px+ to prevent zoom */
  `}

  /* Hide spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const MobileButton = styled(Button)`
  ${(props) =>
    props.$isMobile &&
    `
    transform: scale(1.2);
    margin: 0 8px;
  `}
`;

const QuantityChooser = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "6px" : "4px")};
  border: 1px solid rgb(232, 232, 232);
  border-radius: ${(props) => (props.$isMobile ? "12px" : "8px")};
  overflow: hidden;
  width: fit-content;
  ${(props) =>
    props.$isMobile &&
    `
    transform: scale(1.2);
    margin: 0 16px;
  `}
`;

const ControlButton = styled.div`
  font-size: ${(props) => (props.$isMobile ? "16px" : "12px")};
  padding: ${(props) => (props.$isMobile ? "12px 9px" : "8px 6px")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  user-select: none;
  transition: background-color 0.1s ease;

  /* Touch feedback */
  background-color: ${(props) => {
    if (props.disabled) return "transparent";
    if (props.$isPressed) return "rgba(0, 0, 0, 0.1)";
    return "transparent";
  }};

  &:hover {
    background-color: ${(props) => {
      if (props.disabled) return "transparent";
      if (props.$isPressed) return "rgba(0, 0, 0, 0.1)";
      return "rgba(0, 0, 0, 0.05)";
    }};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const QuantityDisplay = styled.p`
  margin: 0;
  font-size: ${(props) => (props.$isMobile ? "18px" : "14px")};
  line-height: ${(props) => (props.$isMobile ? 2.7 : 2.4)};
  padding-inline: ${(props) => (props.$isMobile ? "9px" : "6px")};
  min-width: ${(props) => (props.$isMobile ? "75px" : "50px")};
  text-align: center;
  color: #1a3353;
  user-select: none;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${(props) => (props.$clickable ? "rgba(0, 0, 0, 0.05)" : "transparent")};
  }

  &:active {
    background-color: ${(props) => (props.$clickable ? "rgba(0, 0, 0, 0.1)" : "transparent")};
  }
`;

export default QuantityControl;
